import React, { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Field, useFormState } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';

import { Input } from '../../../../../../components/common-inputs';
import COUNTRIES from '../../../../../../constants/countries';
import DropdownWithIcons from '../../../../../../components/common-dropdown/DropdownWithIcons';
import MultiselectDropdownNew from '../../../../../../components/multiselect-dropdown/MultiselectDropdownNew';
import FieldWrapper from '../FieldWrapper';
import {
  addNewCategoryOption,
  addNewServiceOption,
  addNewProductOption,
} from '../../../../../../redux/sonar-management/sonarManagementReducer';

import scss from '../../SonarDatasetModal.module.scss';

const GeneralSection = () => {
  const { t } = useTranslation('projects');
  const dispatch = useDispatch();

  const { submitting, values } = useFormState();
  const categoriesOptions = useSelector((state) => state.sonarManagement.categoriesOptions);
  const productsOptions = useSelector((state) => state.sonarManagement.productsOptions);
  const servicesOptions = useSelector((state) => state.sonarManagement.servicesOptions);
  const isEditableDatasetLoading = useSelector((state) => state.sonarManagement.isEditableDatasetLoading);

  const disableFields = isEditableDatasetLoading || submitting;

  const handleAddNewCategoryOption = useCallback(
    (option) => {
      dispatch(addNewCategoryOption(option));
    },
    [dispatch],
  );

  const handleAddNewServiceOption = useCallback(
    (option) => {
      dispatch(addNewServiceOption(option));
    },
    [dispatch],
  );

  const handleAddNewProductOption = useCallback(
    (option) => {
      dispatch(addNewProductOption(option));
    },
    [dispatch],
  );

  return (
    <div className={scss.formSectionContent}>
      <Field name='country'>
        {({ input, meta }) => (
          <FieldWrapper label={t('country')} isShowError={meta.touched && meta.error} errorText={t(meta.error)}>
            <DropdownWithIcons
              value={input.value}
              options={COUNTRIES}
              onSelect={input.onChange}
              name={input.name}
              onBlur={input.onBlur}
              onFocus={input.onFocus}
              isError={meta.touched && meta.error}
              isFilterable
              isEmojiIcons
              disabled={disableFields}
            />
          </FieldWrapper>
        )}
      </Field>

      <Field name='brandCompany'>
        {({ input, meta }) => (
          <FieldWrapper label={t('company')} isShowError={meta.touched && meta.error} errorText={t(meta.error)}>
            <Input size='sm' formProps={input} isError={meta.touched && meta.error} disabled={disableFields} />
          </FieldWrapper>
        )}
      </Field>

      <Field name='domain'>
        {({ input, meta }) => (
          <FieldWrapper label={t('domain')} isShowError={meta.touched && meta.error} errorText={t(meta.error)}>
            <Input size='sm' formProps={input} isError={meta.touched && meta.error} disabled={disableFields} />
          </FieldWrapper>
        )}
      </Field>

      <Field name='categories'>
        {({ input, meta }) => (
          <FieldWrapper label={t('category')} isShowError={meta.touched && meta.error} errorText={t(meta.error)}>
            <MultiselectDropdownNew
              value={input.value}
              options={categoriesOptions}
              onChange={input.onChange}
              name={input.name}
              onBlur={input.onBlur}
              onFocus={input.onFocus}
              isError={meta.touched && meta.error}
              onAddNewOption={handleAddNewCategoryOption}
              disabled={disableFields}
            />
          </FieldWrapper>
        )}
      </Field>

      <Field name='products'>
        {({ input, meta }) => (
          <FieldWrapper label={t('products')} isShowError={meta.touched && meta.error} errorText={t(meta.error)}>
            <MultiselectDropdownNew
              value={input.value}
              options={productsOptions}
              onChange={input.onChange}
              name={input.name}
              onBlur={input.onBlur}
              onFocus={input.onFocus}
              isError={meta.touched && meta.error}
              onAddNewOption={handleAddNewProductOption}
              disabled={disableFields}
            />
          </FieldWrapper>
        )}
      </Field>

      <Field name='services'>
        {({ input, meta }) => (
          <FieldWrapper label={t('services')} isShowError={meta.touched && meta.error} errorText={t(meta.error)}>
            <MultiselectDropdownNew
              value={input.value}
              options={servicesOptions}
              onChange={input.onChange}
              name={input.name}
              onBlur={input.onBlur}
              onFocus={input.onFocus}
              isError={meta.touched && meta.error}
              onAddNewOption={handleAddNewServiceOption}
              disabled={disableFields}
            />
          </FieldWrapper>
        )}
      </Field>
    </div>
  );
};

export default memo(GeneralSection);
